import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import PropTypes from 'prop-types';
import cloneDeep from 'lodash.clonedeep';
import { useReactToPrint } from 'react-to-print';
import FormErrorHandler
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormErrorHandler';
import FormAlertBox
  from '@avtechsoftware/react-component-library/src/components/common/FormErrorHandler/FormAlertBox/FormAlertBox';
import {
  CELSIUS_ID,
  FAHRENHEIT_ID,
  SPEED_FEET,
  SPEED_METERS,
} from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelValueRender';
import DashboardWidget, {
  getAllowedSizes,
  getDefaultContextForType,
  getDefaultSizeForType,
  getWidgetDefinition,
} from './widgets/DashboardWidget';
import { clampToAllowed, DEFAULT_SIZE, FALLBACK_ALLOWED_SIZES } from './widgetSizes';
import PublicDashboardLink from './PublicDashboardLink';
import DashboardPrintable from './DashboardPrintable';
import './Dashboard.css';
import useContainerWidth from './util/useContainerWidth';
import { RaaRoles } from '../../utility/SecurityRules';
import HasRolePrivileges from '../highorder/HasRolePrivileges';

const ReactGridLayout = WidthProvider(GridLayout);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MAX_WIDGETS = 84;
const MAX_WIDGET_AREA = 168;

const COLS = 12;
const COLUMN_WIDTH = 100;
const MARGIN = [12, 12];
const MIN_GRID_WIDTH = COLS * COLUMN_WIDTH + MARGIN[0] * (COLS + 1);
const ROW_HEIGHT = 100;
const PRINT_REFERENCE_WIDTH = 1356;
const MOBILE_BREAKPOINT = 768;
const MAX_ROWS = 20;
const ADD_TILE_KEY = '__add_tile__';
const MISSING_TYPE_MESSAGE = 'Select a tile type before saving.';
const MISSING_TYPE_SUMMARY = 'Some tiles don\u2019t have a type selected yet. Choose a type for each highlighted tile.';
const AUTO_REFRESH_INTERVAL_MS = 30 * 1000;
let nextClientWidgetId = 1;
const makeClientId = () => {
  const id = `c-${nextClientWidgetId}`;
  nextClientWidgetId += 1;
  return id;
};

const TRACKED_WIDGET_FIELDS = [
  'id', 'widgetTypeId', 'positionX', 'positionY', 'sizeX', 'sizeY', 'context',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// eslint-disable-next-line no-underscore-dangle
const widgetKey = (widget) => String(widget.id != null ? widget.id : widget._clientId);

const ensureClientIds = (widgets) => (widgets || []).map((w) => (
  // eslint-disable-next-line no-underscore-dangle
  w.id != null || w._clientId ? w : { ...w, _clientId: makeClientId() }
));

const pickTrackedFields = (widgets) => widgets.map((w) => TRACKED_WIDGET_FIELDS.reduce((acc, f) => {
  acc[f] = w[f];
  return acc;
}, {}));

/**
 * The API returns errors keyed by their position in the *sent* payload, e.g.
 *   { field: "widgets[6].context.title", message: "...", code: "Size" }
 *
 * Return a lookup keyed by widget key -> array of errors *with the widget
 * prefix stripped* so the per-widget FormErrorHandler can address fields
 * relative to its own form (e.g. "context.title").
 */
const WIDGET_ERROR_FIELD_REGEX = /^widgets\[(\d+)\](?:\.(.+))?$/;

const buildErrorsByWidgetKey = (errors, widgets) => {
  const byWidgetKey = new Map();
  (errors || []).forEach((error) => {
    const match = error.field && error.field.match(WIDGET_ERROR_FIELD_REGEX);
    if (!match) return;
    const index = Number(match[1]);
    const subField = match[2] || '';
    const widget = widgets[index];
    if (!widget) return;
    const key = widgetKey(widget);
    if (!byWidgetKey.has(key)) byWidgetKey.set(key, []);
    byWidgetKey.get(key).push({ ...error, field: subField });
  });
  return byWidgetKey;
};

const allowedFor = (widgetTypeId) => getAllowedSizes(widgetTypeId) || FALLBACK_ALLOWED_SIZES;

const defaultSizeFor = (widgetTypeId) => (
  getDefaultSizeForType(widgetTypeId) || DEFAULT_SIZE
);

const findNextPosition = (layoutItems, w, h) => {
  const grid = {};
  layoutItems.forEach((item) => {
    for (let { x } = item; x < item.x + item.w; x += 1) {
      for (let { y } = item; y < item.y + item.h; y += 1) {
        grid[`${x}-${y}`] = true;
      }
    }
  });

  for (let y = 0; y < MAX_ROWS * 4; y += 1) {
    for (let x = 0; x <= COLS - w; x += 1) {
      let fits = true;
      for (let dx = 0; dx < w && fits; dx += 1) {
        for (let dy = 0; dy < h && fits; dy += 1) {
          if (grid[`${x + dx}-${y + dy}`]) fits = false;
        }
      }
      if (fits) return { x, y };
    }
  }
  return { x: 0, y: 0 };
};

const canGrowVertically = (layoutItems, item, newHeight) => {
  const bottom = item.y + newHeight;
  return !layoutItems.some((other) => {
    if (other.i === item.i) return false;
    const horizontalOverlap = other.x < item.x + item.w && other.x + other.w > item.x;
    const directlyBelow = other.y === bottom;
    return horizontalOverlap && directlyBelow;
  });
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Dashboard = ({ dashboard, onSave, isViewOnly }) => {
  const [mounted, setMounted] = useState(false);
  const [gridRef, gridWidth] = useContainerWidth();
  const [mode, setMode] = useState('view');
  const [hasChanges, setHasChanges] = useState(false);
  const [editingWidgetId, setEditingWidgetId] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);
  const [pendingScrollKey, setPendingScrollKey] = useState(null);

  const [dashboardState, setDashboardState] = useState({
    id: null,
    name: 'Untitled Dashboard',
    widgets: [],
  });
  const [savedDashboard, setSavedDashboard] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  // Structured field-level errors returned from the last failed save.
  // Shape: [{ field: 'widgets[6].context.title', message: '...', code: 'Size' }]
  const [saveFieldErrors, setSaveFieldErrors] = useState([]);

  const printRef = useRef(null);
  const gridContainerRef = useRef(null);
  const isEditing = !isViewOnly && mode === 'edit';
  const isMobile = gridWidth != null && gridWidth < MOBILE_BREAKPOINT;
  const usedWidgetArea = useMemo(
    () => dashboardState.widgets.reduce(
      (sum, w) => sum + ((w.sizeX || 0) * (w.sizeY || 0)),
      0,
    ),
    [dashboardState.widgets],
  );

  const wouldExceedAreaOnAdd = (
    usedWidgetArea + (DEFAULT_SIZE.sizeX * DEFAULT_SIZE.sizeY)
  ) > MAX_WIDGET_AREA;
  const isAtWidgetLimit = dashboardState.widgets.length >= MAX_WIDGETS
    || wouldExceedAreaOnAdd;

  // -- Load / hydrate -------------------------------------------------------

  useEffect(() => {
    setMounted(true);
    if (dashboard) {
      const hydrated = {
        ...dashboard,
        widgets: ensureClientIds(dashboard.widgets),
      };
      setDashboardState(hydrated);
      setSavedDashboard(cloneDeep(hydrated));
    }
  }, [dashboard]);

  // -- Auto-refresh ---------------------------------------------------------

  useEffect(() => {
    if (isEditing) return undefined;
    const id = setInterval(() => {
      setRefreshTick((t) => t + 1);
    }, AUTO_REFRESH_INTERVAL_MS);
    return () => clearInterval(id);
  }, [isEditing]);

  // -- Change detection -----------------------------------------------------

  useEffect(() => {
    if (!savedDashboard) return;
    const current = JSON.stringify(pickTrackedFields(dashboardState.widgets));
    const saved = JSON.stringify(pickTrackedFields(savedDashboard.widgets));
    setHasChanges(current !== saved || dashboardState.name !== savedDashboard.name);
  }, [dashboardState, savedDashboard]);

  // Clear a stale save error as soon as the user starts editing again so
  // the red borders/banner don't hang around after they've fixed things.
  useEffect(() => {
    if (saveError) setSaveError(null);
    if (saveFieldErrors.length) setSaveFieldErrors([]);
  }, [dashboardState]);

  // -- Widget mutations -----------------------------------------------------
  const clearPrintScale = useCallback(() => {
    const root = printRef.current;
    if (!root) return;
    root.style.removeProperty('--print-scale');
    root.style.removeProperty('--print-inverse');
  }, []);
  /**
   * Measure the pixel width react-grid-layout actually laid out at, and
   * derive a downscale factor for print. Returns exactly 1 whenever the
   * layout already fits the reference width, so narrower/standard screens
   * print with no transform at all (identical to previous behavior).
   */
  const applyPrintScale = useCallback(() => {
    const root = printRef.current;
    const container = gridContainerRef.current;
    if (!root) return;
    clearPrintScale();
    if (!container) return;

    const gridEl = container.querySelector('.dashboard-grid');
    const layoutWidth = Math.max(
      gridEl ? gridEl.offsetWidth : 0,
      container.scrollWidth,
      container.clientWidth,
    );

    if (!Number.isFinite(layoutWidth) || layoutWidth <= PRINT_REFERENCE_WIDTH) return;

    // Round to 4dp so we don't emit float noise into the style attribute.
    const scale = Math.round((PRINT_REFERENCE_WIDTH / layoutWidth) * 1e4) / 1e4;
    if (scale >= 1) return;

    root.style.setProperty('--print-scale', String(scale));
    root.style.setProperty('--print-inverse', String(1 / scale));
  }, [clearPrintScale]);

  const handleDownloadPdf = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${dashboardState.name || 'Dashboard'} - ${new Date().toISOString().slice(0, 10)}`,
    onBeforeGetContent: () => {
      applyPrintScale();
      return Promise.resolve();
    },
    onAfterPrint: clearPrintScale,
    pageStyle: `
          @page {
            size: 1123px 794px;
            margin: 0;
          }
          @media print {
            html, body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              margin: 0;
              padding: 0;
            }
            .dashboard-printable,
            .dashboard-printable__body,
            .dashboard-grid-container {
              height: auto !important;
              max-height: none !important;
              overflow: visible !important;
            }
          }
        `,
  });

  const addWidget = useCallback(() => {
    const { sizeX, sizeY } = DEFAULT_SIZE;
    const clientId = makeClientId();
    setDashboardState((prev) => {
      if (prev.widgets.length >= MAX_WIDGETS) return prev;
      const currentArea = prev.widgets.reduce(
        (sum, w) => sum + ((w.sizeX || 0) * (w.sizeY || 0)),
        0,
      );
      if (currentArea + (sizeX * sizeY) > MAX_WIDGET_AREA) return prev;
      const currentLayout = prev.widgets.map((widget) => ({
        i: widgetKey(widget),
        x: widget.positionX,
        y: widget.positionY,
        w: widget.sizeX,
        h: widget.sizeY,
      }));
      const { x, y } = findNextPosition(currentLayout, sizeX, sizeY);
      const newWidget = {
        id: null,
        _clientId: clientId,
        widgetTypeId: null,
        positionX: x,
        positionY: y,
        sizeX,
        sizeY,
        context: { title: '', showTitle: true },
      };
      return { ...prev, widgets: [...prev.widgets, newWidget] };
    });
    setPendingScrollKey(String(clientId));
  }, []);

  const deleteWidget = useCallback((key) => {
    setDashboardState((prev) => ({
      ...prev,
      widgets: prev.widgets.filter((w) => widgetKey(w) !== key),
    }));
  }, []);

  const updateWidgetContext = useCallback((key, newContext) => {
    if (!isEditing && onSave) {
      const nextWidgets = dashboardState.widgets.map((w) => (
        widgetKey(w) === key ? { ...w, context: newContext } : w
      ));
      const nextState = { ...dashboardState, widgets: nextWidgets };

      const snapshot = cloneDeep(nextState);
      snapshot.widgets = snapshot.widgets.map((w) => {
        // eslint-disable-next-line no-underscore-dangle
        const { _clientId, ...rest } = w;
        return rest;
      });

      Promise.resolve(onSave(snapshot))
        .then((persisted) => {
          const next = (persisted && Array.isArray(persisted.widgets))
            ? { ...persisted, widgets: ensureClientIds(persisted.widgets) }
            : nextState;
          setDashboardState(next);
          setSavedDashboard(cloneDeep(next));
        })
        .catch(() => {
          // This path has no error UI. Keep the user's change on screen rather
          // than silently dropping it, and avoid an unhandled rejection —
          // DashboardPage.handleSave rethrows a wrapped error.
          setDashboardState(nextState);
        });
      return;
    }

    setDashboardState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) => (
        widgetKey(w) === key ? { ...w, context: newContext } : w
      )),
    }));
  }, [dashboardState, isEditing, onSave]);

  const updateWidgetType = useCallback((key, widgetTypeId) => {
    setDashboardState((prev) => ({
      ...prev,
      widgets: prev.widgets.map((w) => {
        if (widgetKey(w) !== key) return w;
        const preferred = defaultSizeFor(widgetTypeId);
        const snapped = clampToAllowed(
          allowedFor(widgetTypeId),
          preferred.sizeX,
          preferred.sizeY,
        );
        const prevContext = w.context || {};
        const def = getWidgetDefinition(widgetTypeId);
        const typeDefaults = getDefaultContextForType(widgetTypeId) || {};
        const nextTitle = prevContext.title
          ? prevContext.title
          : (def && def.title) || '';
        const nextShowTitle = prevContext.showTitle == null
          ? true
          : prevContext.showTitle;
        return {
          ...w,
          widgetTypeId,
          sizeX: snapped.sizeX,
          sizeY: snapped.sizeY,
          context: {
            ...typeDefaults,
            ...prevContext,
            type: widgetTypeId,
            title: nextTitle,
            showTitle: nextShowTitle,
          },
        };
      }),
    }));
  }, []);

  // -- Persistence ----------------------------------------------------------

  const handleSave = useCallback(async () => {
    const untyped = dashboardState.widgets
      .map((widget, index) => ({ widget, index }))
      .filter(({ widget }) => widget.widgetTypeId == null);

    if (untyped.length > 0) {
      setSaveFieldErrors([
        // field: '' isn't "handled" downstream, so FormAlertBox shows this one.
        { field: '', message: MISSING_TYPE_SUMMARY, code: 'Required' },
        ...untyped.map(({ index }) => ({
          field: `widgets[${index}]`,
          message: MISSING_TYPE_MESSAGE,
          code: 'Required',
        })),
      ]);
      setSaveError(new Error(MISSING_TYPE_SUMMARY));
      setPendingScrollKey(widgetKey(untyped[0].widget));
      return;
    }
    const snapshot = cloneDeep(dashboardState);
    snapshot.widgets = snapshot.widgets.map((w) => {
      // eslint-disable-next-line no-underscore-dangle
      const { _clientId, ...rest } = w;
      return rest;
    });

    if (!onSave) {
      setSavedDashboard(cloneDeep(dashboardState));
      setMode('view');
      return;
    }

    setIsSaving(true);
    setSaveError(null);
    setSaveFieldErrors([]);
    try {
      const persisted = await Promise.resolve(onSave(snapshot));
      // Hydrate from the server response so new widgets pick up their real ids.
      // If the payload is malformed, leave dashboardState alone rather than
      // reassigning it — reinstating pre-save widgets would flip their ids back
      // to null and remount them with widgetId=null, which makes their data
      // hooks short-circuit instead of fetching.
      if (persisted && Array.isArray(persisted.widgets)) {
        const next = { ...persisted, widgets: ensureClientIds(persisted.widgets) };
        setDashboardState(next);
        setSavedDashboard(cloneDeep(next));
      } else {
        setSavedDashboard(cloneDeep(dashboardState));
      }
      setMode('view');
      setEditingWidgetId(null);
    } catch (err) {
      const fieldErrors = (err && Array.isArray(err.errors)) ? err.errors : [];
      setSaveFieldErrors(fieldErrors);
      setSaveError(err || new Error('Failed to save Dashboard.'));
    } finally {
      setIsSaving(false);
    }
  }, [dashboardState, onSave]);

  const handleDiscard = useCallback(() => {
    if (savedDashboard) setDashboardState(cloneDeep(savedDashboard));
    setMode('view');
    setEditingWidgetId(null);
    setSaveError(null);
    setSaveFieldErrors([]);
  }, [savedDashboard]);

  const handlePublicTokenChange = useCallback((newToken) => {
    setDashboardState((prev) => ({ ...prev, publicToken: newToken }));
    setSavedDashboard((prev) => (prev ? { ...prev, publicToken: newToken } : prev));
  }, []);

  useEffect(() => {
    if (!pendingScrollKey) return undefined;

    const container = gridContainerRef.current;
    if (!container) return undefined;

    // Poll frames until the tile's position stops changing, rather than
    // guessing with a fixed number of RAFs. react-grid-layout applies its
    // transforms *and* the grid's own height over one or more passes; scrolling
    // before that settles gets clamped to the old scrollHeight, which is why a
    // tile below the fold appeared not to scroll at all.
    let raf = null;
    let cancelled = false;
    let lastTop = null;
    let stableFrames = 0;
    const deadline = Date.now() + 1000;

    const tick = () => {
      if (cancelled) return;

      const el = container.querySelector(
        `[data-widget-key="${pendingScrollKey}"]`,
      );

      if (!el) {
        if (Date.now() > deadline) {
          setPendingScrollKey(null);
          return;
        }
        raf = requestAnimationFrame(tick);
        return;
      }

      const { top } = el.getBoundingClientRect();
      if (lastTop !== null && Math.abs(top - lastTop) < 0.5) stableFrames += 1;
      else stableFrames = 0;
      lastTop = top;

      if (stableFrames < 2 && Date.now() < deadline) {
        raf = requestAnimationFrame(tick);
        return;
      }

      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingScrollKey(null);
    };

    raf = requestAnimationFrame(tick);

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pendingScrollKey]);

  // -- Layout ---------------------------------------------------------------

  const computedLayout = useMemo(() => {
    if (!isMobile) {
      return dashboardState.widgets.map((widget) => {
        const allowed = allowedFor(widget.widgetTypeId);
        const isResizable = allowed.length > 1;

        const minW = Math.min(...allowed.map((s) => s.sizeX));
        const maxW = Math.max(...allowed.map((s) => s.sizeX));
        const minH = Math.min(...allowed.map((s) => s.sizeY));
        const maxH = Math.max(...allowed.map((s) => s.sizeY));

        return {
          i: widgetKey(widget),
          x: widget.positionX,
          y: widget.positionY,
          w: widget.sizeX,
          h: widget.sizeY,
          minW,
          maxW,
          minH,
          maxH,
          isResizable,
        };
      });
    }
    let runningY = 0;
    return dashboardState.widgets.map((widget) => {
      const item = {
        i: widgetKey(widget),
        x: 0,
        y: runningY,
        w: COLS,
        h: widget.sizeY,
      };
      runningY += widget.sizeY;
      return item;
    });
  }, [dashboardState.widgets, isMobile]);

  const layoutWithAdd = useMemo(() => {
    if (!isEditing) return computedLayout;
    if (isAtWidgetLimit) return computedLayout;

    const addH = 1;
    const addW = isMobile ? COLS : 2;
    const lastY = computedLayout.reduce((max, l) => Math.max(max, l.y + l.h), 0);

    return [
      ...computedLayout,
      {
        i: ADD_TILE_KEY,
        x: 0,
        y: lastY,
        w: addW,
        h: addH,
        isDraggable: false,
        isResizable: false,
      },
    ];
  }, [computedLayout, isMobile, isEditing, isAtWidgetLimit]);

  // Background grid overlay shown while editing. Matches RGL's own column
  // pitch: each column is (containerWidth - margin*(cols+1)) / cols wide,
  // separated by MARGIN[0] gutters; rows are ROW_HEIGHT tall with MARGIN[1]
  // gutters. We paint each cell as a translucent rectangle so gutters stay
  // clear, which mirrors what RGL's placeholder snaps to.
  const gridOverlayStyle = useMemo(() => {
    if (!isEditing || isMobile || !gridWidth) return null;
    // RGL lays out at the container's rendered width, which is pinned to at
    // least MIN_GRID_WIDTH on desktop/tablet via the CSS media query. Our
    // hook reports the *raw* (possibly narrower, scrollable) width, so clamp
    // up to MIN_GRID_WIDTH here or the overlay lines won't match the tiles.
    const layoutWidth = Math.max(gridWidth, MIN_GRID_WIDTH);
    const colWidth = (layoutWidth - MARGIN[0] * (COLS + 1)) / COLS;
    const cellW = colWidth + MARGIN[0];
    const cellH = ROW_HEIGHT + MARGIN[1];
    const line = 'rgba(122, 167, 255, 0.45)';
    return {
      backgroundImage: `
            linear-gradient(to right, ${line} 0 1px, transparent 1px 100%),
            linear-gradient(to bottom, ${line} 0 1px, transparent 1px 100%)
          `,
      backgroundSize: `${cellW}px 100%, 100% ${cellH}px`,
      backgroundPosition: `${MARGIN[0] / 2}px 0, 0 ${MARGIN[1] / 2}px`,
      backgroundRepeat: 'repeat',
    };
  }, [isEditing, isMobile, gridWidth]);

  // -- Grid callbacks -------------------------------------------------------

  const widgetsByKey = useMemo(() => {
    const map = new Map();
    dashboardState.widgets.forEach((widget) => {
      map.set(widgetKey(widget), widget);
    });
    return map;
  }, [dashboardState.widgets]);

  // Errors bucketed by widget key (with the "widgets[N]." prefix stripped),
  // so each widget's inner FormErrorHandler sees only its own errors and
  // can address fields like "context.title" directly.
  const errorsByWidgetKey = useMemo(
    () => buildErrorsByWidgetKey(saveFieldErrors, dashboardState.widgets),
    [saveFieldErrors, dashboardState.widgets],
  );

  // The fieldList passed to the *top-level* FormErrorHandler. Any error whose
  // `field` is in this list is considered "handled" by a downstream ErrorWrapper
  // (per-widget FormErrorHandlers) and therefore NOT surfaced by the top-level
  // FormAlertBox. Anything else (empty field, unrecognized prefix, orphan
  // widget index, etc.) falls through and shows as a generic banner error.
  const handledFieldList = useMemo(
    () => (saveFieldErrors || [])
      .filter((e) => WIDGET_ERROR_FIELD_REGEX.test(e.field || ''))
      .map((e) => e.field),
    [saveFieldErrors],
  );

  const handleLayoutChange = useCallback((newLayout) => {
    // Skip until we have a real measurement, and never persist on the phone
    // tier (its layout is a display-only stack).
    if (!isEditing || isMobile || gridWidth == null) return;

    setDashboardState((prev) => {
      let mutated = false;
      const widgets = prev.widgets.map((widget) => {
        const key = widgetKey(widget);
        const li = newLayout.find((l) => l.i === key && l.i !== ADD_TILE_KEY);
        if (!li) return widget;
        if (li.x === widget.positionX && li.y === widget.positionY) return widget;
        mutated = true;
        return { ...widget, positionX: li.x, positionY: li.y };
      });
      return mutated ? { ...prev, widgets } : prev;
    });
  }, [isEditing, isMobile, gridWidth]);

  const handleResize = useCallback((_layout, oldItem, newItem, placeholder) => {
    const widget = widgetsByKey.get(newItem.i);
    if (!widget) return;

    const allowed = allowedFor(widget.widgetTypeId);
    const snapped = clampToAllowed(allowed, newItem.w, newItem.h);

    const allowVerticalGrowth = snapped.sizeY <= oldItem.h
        || canGrowVertically(computedLayout, oldItem, snapped.sizeY);
    const finalSizeY = allowVerticalGrowth ? snapped.sizeY : oldItem.h;

    /* eslint-disable no-param-reassign */
    newItem.w = snapped.sizeX;
    newItem.h = finalSizeY;
    if (placeholder) {
      placeholder.w = snapped.sizeX;
      placeholder.h = finalSizeY;
    }
    /* eslint-enable no-param-reassign */
  }, [computedLayout, widgetsByKey]);

  const handleResizeStop = useCallback((_layout, oldItem, newItem) => {
    const widget = widgetsByKey.get(newItem.i);
    if (!widget) return;

    const allowed = allowedFor(widget.widgetTypeId);
    const snapped = clampToAllowed(allowed, newItem.w, newItem.h);

    const allowVerticalGrowth = snapped.sizeY <= oldItem.h
        || canGrowVertically(computedLayout, oldItem, snapped.sizeY);
    const finalSizeY = allowVerticalGrowth ? snapped.sizeY : oldItem.h;

    const targetKey = newItem.i;
    setDashboardState((prev) => {
      const widgets = prev.widgets.map((w) => {
        if (widgetKey(w) !== targetKey) return w;
        return {
          ...w,
          positionX: newItem.x,
          positionY: newItem.y,
          sizeX: snapped.sizeX,
          sizeY: finalSizeY,
        };
      });
      return { ...prev, widgets };
    });
  }, [computedLayout, widgetsByKey]);

  // -- Rendering ------------------------------------------------------------

  const renderWidgets = () => dashboardState.widgets.map((widget) => {
    const key = widgetKey(widget);
    const isEditingThis = editingWidgetId === key;
    const allowed = allowedFor(widget.widgetTypeId);
    const isResizable = allowed.length > 1;
    const widgetErrors = errorsByWidgetKey.get(key) || [];
    return (
      <div key={key} data-widget-key={key}>
        <DashboardWidget
          key={`${key}-${widget.widgetTypeId == null ? 'none' : widget.widgetTypeId}`}
          widgetTypeId={widget.widgetTypeId}
          widgetId={widget.id}
          context={widget.context}
          isEditMode={isEditing}
          isEditingThis={isEditingThis}
          isViewOnly={isViewOnly}
          sizeX={widget.sizeX}
          sizeY={widget.sizeY}
          refreshTick={refreshTick}
          onToggleEdit={() => setEditingWidgetId(isEditingThis ? null : key)}
          onRequestEditMode={() => setMode('edit')}
          onDelete={() => deleteWidget(key)}
          onContextChange={(newContext) => updateWidgetContext(key, newContext)}
          onTypeChange={(newType) => updateWidgetType(key, newType)}
          isResizable={isResizable}
          userPreferences={dashboardState.userPreferences}
          errors={widgetErrors}
        />
      </div>
    );
  });

  const renderAddTile = () => (
    <div key={ADD_TILE_KEY} className="dashboard-add-tile-item">
      <button
        type="button"
        className="dashboard-add-tile"
        onClick={addWidget}
      >
        + Add Tile
      </button>
    </div>
  );

  if (!mounted) return null;

  // If the save failed but the API didn't attach any structured field errors,
  // synthesize a single generic entry so FormAlertBox still shows something.
  // eslint-disable-next-line no-nested-ternary
  const topLevelErrorList = saveFieldErrors.length > 0
    ? saveFieldErrors
    : (saveError ? [{ field: '', message: 'Failed to save Dashboard. Please try again.' }] : []);

  return (
    <div className="dashboard-root">
      {!isViewOnly && topLevelErrorList.length > 0 && isEditing && (
        <FormErrorHandler
          errorList={topLevelErrorList}
          fieldList={handledFieldList}
          noDivTags
        >
          <div className="dashboard-error-row">
            <FormAlertBox />
          </div>
        </FormErrorHandler>
      )}
      {!isViewOnly && (
        <div className="dashboard-header">
          <div className="dashboard-header__actions">
            {isEditing ? (
              <>
                {!isAtWidgetLimit && (
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--secondary"
                    onClick={addWidget}
                    disabled={isSaving}
                  >
                    + Add Tile
                  </button>
                )}
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--link"
                  onClick={handleDiscard}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="dashboard-btn dashboard-btn--primary"
                  onClick={handleSave}
                  disabled={!hasChanges || isSaving}
                >
                  {isSaving ? 'Saving…' : 'Save Changes'}
                </button>
              </>
            ) : (
              <>
                {dashboardState.id && (
                  <HasRolePrivileges targetRoleList={[RaaRoles.MANAGER, RaaRoles.ADMIN]}>
                    <PublicDashboardLink
                      dashboardId={dashboardState.id}
                      publicToken={dashboardState.publicToken}
                      onTokenChange={handlePublicTokenChange}
                    />
                  </HasRolePrivileges>
                )}

                <button
                  type="button"
                  className="ml-3 dashboard-btn dashboard-btn--secondary"
                  onClick={handleDownloadPdf}
                >
                  Download PDF
                </button>

                <button
                  type="button"
                  className="ml-3 dashboard-btn dashboard-btn--primary dashboard-header__customize"
                  onClick={() => setMode('edit')}
                >
                  Customize
                </button>
              </>
            )}
          </div>
        </div>
      )}
      <DashboardPrintable ref={printRef}>
        <div
          ref={(node) => {
            // Same element, two consumers: measurement + scroll-into-view.
            gridContainerRef.current = node;
            gridRef(node);
          }}
          className={`dashboard-grid-container${isEditing ? ' dashboard-grid-container--edit' : ''}`}
          style={gridOverlayStyle || undefined}
        >
          <ReactGridLayout
            className={`dashboard-grid${isEditing ? ' dashboard-grid--edit' : ''}`}
            layout={layoutWithAdd}
            cols={COLS}
            margin={MARGIN}
            rowHeight={ROW_HEIGHT}
            compactType="vertical"
            preventCollision={false}
            isBounded
            resizeHandles={isEditing ? ['se'] : []}
            isDraggable={isEditing && !isMobile}
            isResizable={isEditing && !isMobile}
            draggableHandle=".widget__drag-handle"
            onResize={handleResize}
            onResizeStop={handleResizeStop}
            onLayoutChange={handleLayoutChange}
            maxRows={MAX_ROWS}
          >
            {renderWidgets()}
            {isEditing && !isAtWidgetLimit && renderAddTile()}
          </ReactGridLayout>
        </div>
      </DashboardPrintable>
    </div>
  );
};

Dashboard.propTypes = {
  dashboard: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    widgets: PropTypes.array,
    userPreferences: PropTypes.shape({
      temperatureScale: PropTypes.oneOf([CELSIUS_ID, FAHRENHEIT_ID]),
      airSpeedScale: PropTypes.oneOf([SPEED_METERS, SPEED_FEET]),
    }),
  }),
  onSave: PropTypes.func,
  isViewOnly: PropTypes.bool,
};

Dashboard.defaultProps = {
  dashboard: null,
  onSave: null,
  isViewOnly: false,
};

export default Dashboard;
